import React from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { minutesToHours } from "../../../../util/function";

const nameExpan = {
  ConsultationRoom: "Consultation Room",
  OperationTheatre: "Operation Theatre",
  Kennel: "Kennel",
  BathBays: "Bath Bays",
  HomeKit: "Home Kit"
}

const FacilityUtilization = () => {
  const { loading, facilityUtilization } =
    useSelector((state) => state.appointmentTypes);

  const categories = facilityUtilization?.byFacilityType?.map((ft) => nameExpan?.[ft?.facilityType] ?? '') || [];
  const seriesData = facilityUtilization?.byFacilityType?.map(
    (ft) => Number(ft?.overallUtilizationPercent ?? 0)
  ) || [];

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: "25%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
    },
    xaxis: {
      categories,
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "13px",
          colors: "#64748B",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "12px",
        },
      },
    },
    colors: [
      "#1D4ED8",
      "#9333EA",
      "#8B5CF6",
      "#06B6D4",
      "#A78BFA",
      "#EC4899",
    ],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.3,
        gradientToColors: [
          "#1D4ED8",
          "#9333EA",
          "#8B5CF6",
          "#06B6D4",
          "#A78BFA",
          "#EC4899",
        ],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.9,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const label = categories?.[dataPointIndex] || 'Unknown';
        const hoverData = facilityUtilization?.byFacilityType?.[dataPointIndex] ?? {};
        const bookedMins = `${hoverData?.overallUtilizationPercentFormatted || '0%'} (${minutesToHours(hoverData?.totalBookedMinutes ?? 0)})`;

        return (
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          label + ` (${hoverData?.totalFacilities || 0})` +
          '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          (w.globals.colors?.[dataPointIndex] || '#1D4ED8') +
          '; margin-left: 5px;"></span>' +
          "</div>" +
          '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
          '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Utilization: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          bookedMins +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Average Downtime: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          (hoverData?.avgDowntimeFormatted || 'N/A') +
          "</span>" +
          "</div>" +
          "</div>" +
          "</div>"
        );
      },
    },
    grid: {
      borderColor: "#E2E8F0",
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="rounded-lg shadow border border-gray-200 bg-white p-6">
        Loading...
      </div>
    );
  }

  // Ensure we have valid data before rendering the chart
  if (!categories || !seriesData || categories?.length === 0 || seriesData?.length === 0) {
    return (
      <div className="rounded-lg shadow border border-gray-200 bg-white p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Facility Utilization
          </h3>
        </div>
        <p className="text-gray-500">No data available for Facility Utilization.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg shadow border border-gray-200 bg-white p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Facility Utilization
        </h3>
      </div>
      <ReactApexChart
        options={options}
        series={[{ name: "Booked Minutes", data: seriesData }]}
        type="bar"
        height={320}
      />
    </div>
  );
};

export default FacilityUtilization;
